import React, { Suspense, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Car, 
  Wrench, 
  Award, 
  BookOpen, 
  Users, 
  Calendar,
  ArrowLeft,
  GraduationCap,
  Settings,
  Zap,
  Gauge,
  Shield,
  Cog
} from 'lucide-react';
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
  Stars,
  PerspectiveCamera
} from "@react-three/drei";

// Préchargement du modèle
useGLTF.preload("/models/mclaren_f1_1993_by_alex.ka.glb");

// Modèle 3D
function IceCreamShopModel() {
  const { scene } = useGLTF("/models/mclaren_f1_1993_by_alex.ka.glb");
  const ref = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    ref.current.rotation.y = t * 0.2;
  });
  
  return (
    <primitive
      ref={ref}
      object={scene}
      scale={[0.5, 0.5, 0.5]}
      position={[0, -1, 0]}
    />
  );
}

// Scène 3D
function Scene3D() {
  return (
    <Canvas
      shadows
      className="fixed inset-0 -z-10"
      dpr={[1, 2]}>
      <color attach="background" args={["#1e1e2e"]} />
      <fog attach="fog" args={["#1e1e2e", 5, 15]} />
      <ambientLight intensity={0.5} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
      
      <Suspense fallback={null}>
        <PerspectiveCamera makeDefault position={[0, 1, 10]} fov={40} />
        
        <Stage environment="city">
          <Center>
            <IceCreamShopModel />
          </Center>
        </Stage>
        
        <Environment preset="city" />
        <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.5} />
        <Stars count={5000} depth={50} fade speed={1.5} />
      </Suspense>
    </Canvas>
  );
}

const Automobile = () => {
  const formations = [
    {
      icon: <Wrench className="w-8 h-8 text-red-400" />,
      title: "CAP Maintenance des Véhicules Option VP (Voitures Particulières)",
      duration: "2 ans",
      niveau: "Niveau 3 (CAP)",
      description: "Formation complète aux techniques de maintenance et de réparation automobile.",
      competences: [
        "Réaliser l'entretien périodique des véhicules",
        "Effectuer le diagnostic des pannes mécaniques",
        "Remplacer les pièces défectueuses",
        "Contrôler et régler les équipements du véhicule"
      ]
    },
    {
      icon: <Car className="w-8 h-8 text-orange-400" />,
      title: "Bac Pro Maintenance des Véhicules Option VP",
      duration: "3 ans",
      niveau: "Niveau 4 (Bac)",
      description: "Formation approfondie à la maintenance automobile avec expertise technique.",
      competences: [
        "Diagnostiquer les dysfonctionnements complexes",
        "Réaliser des opérations de maintenance préventive et corrective",
        "Organiser la maintenance des véhicules",
        "Gérer la relation client et conseiller"
      ]
    },
    {
      icon: <Cog className="w-8 h-8 text-cyan-400" />,
      title: "CQP TAVA - Technicien Expert Après-Vente Automobile",
      duration: "1 an",
      niveau: "Niveau 4 (Bac)",
      isCQP: true,
      description: "Certificat de Qualification Professionnelle reconnu par la branche automobile pour devenir technicien expert en après-vente.",
      competences: [
        "Réaliser le diagnostic approfondi des systèmes complexes",
        "Effectuer des interventions techniques de haut niveau",
        "Conseiller la clientèle sur les réparations et l'entretien",
        "Gérer les interventions sur véhicules récents et électriques"
      ]
    }
  ];

  const debouches = [
    {
      icon: <Settings className="w-6 h-6 text-red-400" />,
      title: "Mécanicien auto",
      description: "Réparer et entretenir les véhicules en atelier"
    },
    {
      icon: <Gauge className="w-6 h-6 text-orange-400" />,
      title: "Technicien de maintenance",
      description: "Diagnostiquer et résoudre les pannes complexes"
    },
    {
      icon: <Zap className="w-6 h-6 text-yellow-400" />,
      title: "Électricien auto",
      description: "Spécialiste des systèmes électriques et électroniques"
    },
    {
      icon: <Shield className="w-6 h-6 text-cyan-400" />,
      title: "Contrôleur technique",
      description: "Vérifier la conformité et la sécurité des véhicules"
    },
    {
      icon: <Award className="w-6 h-6 text-green-400" />,
      title: "Technicien expert après-vente",
      description: "Expert en diagnostic et réparation de systèmes complexes"
    }
  ];

  const avantages = [
    {
      icon: <Car className="w-6 h-6 text-red-400" />,
      title: "Secteur en évolution",
      description: "Technologies innovantes et véhicules électriques en plein essor"
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-orange-400" />,
      title: "Formation pratique",
      description: "Travaux sur véhicules réels et équipements professionnels"
    },
    {
      icon: <Award className="w-6 h-6 text-yellow-400" />,
      title: "Emploi garanti",
      description: "Un secteur qui recrute avec de nombreuses opportunités"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-40 bg-gradient-to-br from-slate-900 to-slate-800"
    >
      {/* Scène 3D en arrière-plan */}
      <div className="fixed inset-0 z-0">
        <Scene3D />
      </div>
      
      <Loader />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Bouton retour */}
        <Link to="/">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mb-8 px-6 py-3 bg-slate-800/60 backdrop-blur-sm text-white rounded-lg flex items-center space-x-2 hover:bg-slate-700/60 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </motion.button>
        </Link>

        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <Car className="w-16 h-16 text-red-400" />
            <h1 className="text-5xl font-bold text-white">Pôle Automobile</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Formez-vous aux métiers de la maintenance automobile et devenez expert en mécanique et systèmes électroniques
          </p>
        </motion.div>

        {/* Formations */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Nos Formations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {formations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className={`bg-slate-800/60 backdrop-blur-sm p-8 rounded-lg border-l-4 ${
                  formation.isCQP ? 'border-cyan-500' : 'border-red-500'
                } hover:shadow-lg hover:shadow-${formation.isCQP ? 'cyan' : 'red'}-500/20 transition-all`}
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {formation.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">{formation.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`px-3 py-1 ${
                        formation.isCQP ? 'bg-cyan-600/30 text-cyan-300 border-cyan-400/30' : 'bg-red-600/30 text-red-300 border-red-400/30'
                      } rounded-full text-sm border`}>
                        {formation.duration}
                      </span>
                      <span className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-sm border border-purple-400/30">
                        {formation.niveau}
                      </span>
                      {formation.isCQP && (
                        <span className="px-3 py-1 bg-yellow-600/30 text-yellow-300 rounded-full text-sm border border-yellow-400/30">
                          CQP
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6 text-sm">{formation.description}</p>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center space-x-2 text-sm">
                    <BookOpen className={`w-4 h-4 ${formation.isCQP ? 'text-cyan-400' : 'text-red-400'}`} />
                    <span>Compétences :</span>
                  </h4>
                  <ul className="space-y-2">
                    {formation.competences.map((comp, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-300 text-sm">
                        <span className={`${formation.isCQP ? 'text-cyan-400' : 'text-red-400'} mt-1`}>•</span>
                        <span>{comp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Débouchés */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Débouchés Professionnels</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {debouches.map((debouche, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-red-500/20 transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {debouche.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{debouche.title}</h3>
                <p className="text-gray-300 text-sm">{debouche.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Avantages */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Pourquoi choisir nos formations ?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {avantages.map((avantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-red-500/20 transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {avantage.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{avantage.title}</h3>
                <p className="text-gray-300 text-sm">{avantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="text-center"
        >
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-lg font-semibold"
            >
              Nous contacter pour plus d'informations
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Automobile;